import {
  differenceInYears,
  format,
  formatDate,
  isAfter,
  isBefore,
  isToday,
  parseISO,
} from "date-fns";
import { IBackendError, IFormattedFile } from "library/types";
import i18next from "i18next";
import { toast } from "react-toastify";
import {
  determineCity,
  determineSagautaVehicleClass,
  transformForForm,
} from "./utils";
import {
  CalculationOrOfferResponseDto,
  formInitialState,
  InsurerListItems,
  InsurerSpecificInfosDto,
} from "library/types/types_old";
import { configObject } from "library/utils/config";

export const debounce = (callback, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      callback(...args);
    }, delay);
  };
};

export const EMAIL_ID_SEPARATOR = "###";

export const formatDateString = (date?: string) => {
  return date !== null && date !== "" && date
    ? format(new Date(date), "dd.MM.yyyy")
    : null;
};
export const formatFullDate = (date: string) => {
  return date !== null && date !== ""
    ? format(new Date(date), "dd.MM.yyyy hh:mm:ss")
    : "";
};

export const formatDateWithTime = (date: Date) =>
  format(date, "yyyy_MM_dd_kk_mm_ss");

export const firstLetterUpper = (string: string) => {
  return string && string.charAt(0).toUpperCase() + string.slice(1);
};

export const formatNumber = (
  n: number,
  minPrecision = 2,
  maxPrecision = 2,
  returnValue = ""
) => {
  if (!n) return "";
  return n.toLocaleString("de-DE", {
    minimumFractionDigits: minPrecision,
    maximumFractionDigits: maxPrecision,
  });
};

export const getTaxonomyValue = (
  options: {
    value: string | number;
    label: string;
  }[],
  fieldValue: string,
  t: any
) => t(options?.find((option) => option.value === fieldValue)?.label);

export const urlFormat = (url?: string) => {
  if (!url) return "";

  let result = url;
  if (url.charAt(url.length - 1) === "/") {
    result = url.slice(0, -1);
  }
  return result;
};

export const openLinkInNewTab = (path: string) => {
  return window.open(path);
};

export const genUUID = () => {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    var r = (Math.random() * 16) | 0,
      v = c == "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

export const formBase64String = (file: File) => {
  const reader = new FileReader();

  return new Promise<string>((resolve, reject) => {
    reader.onerror = () => {
      reader.abort();
      reject(new DOMException("Problem parsing file"));
    };

    reader.onload = () => {
      resolve(
        reader.result
          ? typeof reader.result === "string"
            ? reader.result.substring(reader.result.indexOf(",") + 1)
            : ""
          : ""
      );
    };
    reader.readAsDataURL(file);
  });
};

export const formatFile = async (file) => {
  if (!file) return null;
  const data = {
    file: file,
    preview: await formBase64String(file),
    name: file.name,
    base64String: await formBase64String(file),
    type: file.type,
  };
  return data as IFormattedFile;
};

export function deepEqual(object1 = {}, object2 = {}) {
  const keys1 = Object.keys(object1);
  const keys2 = Object.keys(object2);
  if (keys1.length !== keys2.length) {
    return false;
  }
  for (const key of keys1) {
    const val1 = object1[key];
    const val2 = object2[key];
    const areObjects = isObject(val1) && isObject(val2);
    if (
      (areObjects && !deepEqual(val1, val2)) ||
      (!areObjects && val1 !== val2)
    ) {
      return false;
    }
  }
  return true;
}
export function isObject(object) {
  return object != null && typeof object === "object";
}

export const getAxiosError = (err?: any) => {
  const response = err.response;
  if (response) {
    const responseData = err.response.data;
    if (responseData) {
      const env = process.env.REACT_APP_ENVIRONMENT as string;
      const developmentEnvironments = ["L", "D", "T"];
      const isDevOrTestEnv = developmentEnvironments.includes(env);
      if (Array.isArray(responseData)) {
        let message = "";

        responseData.forEach((e: IBackendError, index) => {
          const translatedMessageVariables =
            e.messageVariables?.map((item: string) => {
              return i18next.t(item);
            }) || [];

          const backendMessage =
            e?.exceptionMessage || e?.formatedMessage + ". \r\n";
          if (isDevOrTestEnv) {
            message += backendMessage;
          } else {
            const translatedMessage = i18next.t(e.messageCode || "", {
              replace: translatedMessageVariables,
            });
            if (
              translatedMessage.toString().trim() ===
              (e.messageCode ? e.messageCode.toString().trim() : "")
            ) {
              // this means that we dont have translation for this message
              message += backendMessage;
            } else {
              message += translatedMessage + ". \r\n";
            }
          }
        });

        return message;
      }
      //
      else {
        const messageCode = responseData.messageCode;
        if (messageCode) {
          const backendMessage =
            responseData?.exceptionMessage || responseData?.formatedMessage;
          if (isDevOrTestEnv) {
            return i18next.t(backendMessage);
          }

          const translatedMessageVariables = responseData.messageVariables.map(
            (item: string) => {
              return i18next.t(item);
            }
          );

          const translatedMessage = i18next.t(
            messageCode,
            translatedMessageVariables as any
          );

          if (translatedMessage?.toString().trim() === messageCode?.trim()) {
            // this means that we dont have translation for this message
            return i18next.t(backendMessage);
          }

          // const toastMessage = translatedMessageVariables.length
          //   ? translatedMessageVariables
          //   : message;

          return i18next.t(messageCode, translatedMessageVariables);
        } else {
          if (responseData.message) {
            return responseData.message;
          } else {
            return responseData;
          }
        }
      }
    } else {
      return err?.toString();
    }
  } else {
    return err?.message?.toString() || err?.statusText?.toString();
  }
};
export const handleAxiosError = (err?: any) => {
  const error = getAxiosError(err);
  toast.error(error);
};

export const formatSelectFieldForPayload = ({ values, field }) => {
  return values[field]?.value ? values[field].value : values[field];
};

export const formatDateFieldForPayload = (value) => {
  try {
    if (!value) return;
    return formatDate(value, "yyyy-MM-dd");
  } catch (error) {
    console.log(value, "value");
    console.log(error, "value");
  }
};

// export const canSeePage = ({ roleList, route }) => {
//   return roleList?.some((role) => PAGES_PER_ROLES[role]?.pages.includes(route));
// };

export const downloadFile = (data: any, fileName?: string) => {
  const link = document.createElement("a");
  link.href = data?.url;
  if (data?.url) {
    // const fileName = data?.url.split("/").pop().split("?")[0];
    // openLinkInNewTab(data?.url);
    link.setAttribute("download", fileName || "");
    document.body.appendChild(link);
    link.click();
    link.remove();
  }
};

export const getBase64 = async (file: Blob): Promise<string | undefined> => {
  var reader = new FileReader();
  reader.readAsDataURL(file as Blob);

  return new Promise((resolve, reject) => {
    reader.onload = () => {
      const dataUrl = reader.result as any;
      const base64String = dataUrl?.split(",")[1];
      resolve(base64String);
    };
    reader.onerror = (error) => reject(error);
  });
};

export const loadFromLocalStorage = (key: string, defaultValue: any) => {
  const storedValue = localStorage.getItem(key);
  return storedValue ? JSON.parse(storedValue) : defaultValue;
};

// Function to save to localStorage
export const saveToLocalStorage = (key: string, value: any) => {
  localStorage.setItem(key, JSON.stringify(value));
};

export const isInteger = (str) => {
  return /^-?\d+$/.test(str);
};

export function isBeforeOrOnToday(date) {
  return isBefore(date, new Date()) || isToday(date);
}

export function isAfterOrOnToday(date) {
  return isAfter(date, new Date()) || isToday(date);
}

export const calculateAge = (birthDateString: string) => {
  const birthDate = parseISO(birthDateString); // Parse the ISO string into a Date object
  const age = differenceInYears(new Date(), birthDate); // Calculate age
  return age;
};

export function getExtensionFromUrl(url) {
  // Use a regular expression to match the extension
  const match = url.match(/\/[^/]*\.(\w+)(\?|$)/);
  return match ? match[1] : null;
}

export function extractFileNameFromUrl(url) {
  try {
    // Extract the path from the URL (ignores query parameters)
    const path = url.split("?")[0];
    // Extract the filename by splitting the path on `/` and taking the last part
    const fileName = path.split("/").pop();
    return fileName;
  } catch (error) {
    console.error("Error extracting filename:", error);
    return null;
  }
}

export function extractFileNameFromUrlWithoutExtension(url) {
  try {
    // Extract the path from the URL (ignores query parameters)
    const path = url.split("?")[0];
    // Extract the filename by splitting the path on `/` and taking the last part
    const fileName = path.split("/").pop();
    const fileNameWithoutExtension = fileName.split(".")[0];
    return fileNameWithoutExtension;
  } catch (error) {
    console.error("Error extracting fileNameWithoutExtension:", error);
    return null;
  }
}
export const transformForFormVehicle = ({ offer, t, taxonomy }) => {
  // If 'offer' or its Request is missing, return the empty initial form
  if (!offer?.Request) {
    const emptyClone = JSON.parse(JSON.stringify(formInitialState));
    return transformForForm({
      obj: emptyClone,
      t,
      taxonomy,
      config: configObject,
    });
  }

  const {
    Request: {
      VehicleInfo = {},
      AdditionalCalculationInfo = {},
      InsurerSpecificInfos = {},
    },
    Response = {},
  } = offer;

  // Start with the base form state
  let result = { ...formInitialState };

  // Populate with vehicle info
  if (VehicleInfo) {
    Object.assign(result, {
      VehicleVin: VehicleInfo.VehicleVin,
      VehicleLicensePlate: VehicleInfo.VehicleLicensePlate,
      OwnerId: VehicleInfo.OwnerId,
      VehiclePowerKw: VehicleInfo.VehiclePowerKw,
      VehicleCubicCapacity: VehicleInfo.VehicleCubicCapacity,
      VehicleMaximumGrossWeight: VehicleInfo.VehicleMaximumGrossWeight,
      VehicleSeats: VehicleInfo.VehicleSeats,
      VehicleWeight: VehicleInfo.VehicleWeight,
      VehicleManufacturedOn: VehicleInfo.VehicleManufacturedOn,
      VehicleFirstRegistrationOn: VehicleInfo.VehicleFirstRegistrationOn,
      VehicleBrand: VehicleInfo.VehicleBrand,
      VehicleModel: VehicleInfo.VehicleModel,
      OwnerFirstName: VehicleInfo.OwnerFirstName,
      OwnerLastName: VehicleInfo.OwnerLastName,
      OwnerBirthDate: VehicleInfo.OwnerBirthDate,
      OwnerCountryCode: VehicleInfo.OwnerCountryCode,
      OwnerAddress: VehicleInfo.OwnerAddress,
    });
  }

  // Populate with additional calculation info
  if (AdditionalCalculationInfo) {
    Object.assign(result, {
      VehicleClass: AdditionalCalculationInfo.VehicleClass,
      VehicleUsage: AdditionalCalculationInfo.VehicleUsage,
      OwnerDrivingLicenseDate:
        AdditionalCalculationInfo.OwnerDrivingLicenseDate,
      OwnerCity: AdditionalCalculationInfo.OwnerCity,
      OwnerEmail: AdditionalCalculationInfo.OwnerEmail,
      OwnerTelephone: AdditionalCalculationInfo.OwnerTelephone,
      OtherDrivers: AdditionalCalculationInfo.OtherDrivers,
      OtherDriverAge: AdditionalCalculationInfo.OtherDriverAge,
      OtherDriverExperience: AdditionalCalculationInfo.OtherDriverExperience,
      PolicyStartDate: AdditionalCalculationInfo.PolicyStartDate,
      PolicyPeriod: AdditionalCalculationInfo.PolicyPeriod,
      PaymentCount: AdditionalCalculationInfo.PaymentCount,
    });
  }

  // Populate with insurer-specific infos
  if (InsurerSpecificInfos) {
    Object.assign(result, {
      Compensa_PolicyCoverageEURAndGreenCardCountries:
        InsurerSpecificInfos.Compensa?.PolicyCoverageEURAndGreenCardCountries,
      Compensa_MiniCASCO: InsurerSpecificInfos.Compensa?.MiniCASCO,
      Ergo_DiscountPercent: InsurerSpecificInfos.Ergo?.DiscountPercent,
      Ergo_CompanyType: InsurerSpecificInfos.Ergo?.CompanyType,
      Gjensidige_ApplyMaxDiscount:
        InsurerSpecificInfos.Gjensidige?.ApplyMaxDiscount,
      Gjensidige_DiscountPercent:
        InsurerSpecificInfos.Gjensidige?.DiscountPercent,
      Gjensidige_SpecialUsage: InsurerSpecificInfos.Gjensidige?.SpecialUsage,
      LietuvosDraudimas_SpecialUsage:
        InsurerSpecificInfos.LietuvosDraudimas?.SpecialUsage || null,
    });
  }

  // Ensure sensible defaults if no calculation info provided
  const tomorrowIso = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();

  result.VehicleClass =
    result.VehicleClass ??
    determineSagautaVehicleClass(
      VehicleInfo.VehicleClass,
      VehicleInfo.VehicleClassB
    );
  result.OwnerCity =
    result.OwnerCity ?? determineCity(VehicleInfo.OwnerAddress, taxonomy);
  result.Ergo_DiscountPercent = result.Ergo_DiscountPercent ?? 0;
  result.Gjensidige_DiscountPercent = result.Gjensidige_DiscountPercent ?? 0;
  result.PolicyPeriod = result.PolicyPeriod ?? 12;
  result.PolicyStartDate = result.PolicyStartDate ?? tomorrowIso;
  result.VehicleUsage = result.VehicleUsage ?? 1;
  result.PaymentCount = result.PaymentCount ?? 1;

  const insurerListItems = prepareInsurerListItems(
    InsurerSpecificInfos,
    Response
  );
  Object.assign(result, {
    InsurerListItems: insurerListItems,
  });

  // Finally transform and return
  const finalClone = JSON.parse(JSON.stringify(result));
  return transformForForm({
    obj: finalClone,
    t,
    taxonomy,
    config: configObject,
  });
};

export const prepareInsurerListItems = (
  insurerSpecificInfos: InsurerSpecificInfosDto,
  response: CalculationOrOfferResponseDto
): InsurerListItems => {
  const resultinsurerListItems: InsurerListItems = {
    Balcia: {
      IncludeExtendedRoadAssistance: null,
      IncludeDriverAccidentInsurance: null,
      IncludeDriverAndPassingerAccidentInsurance: null,
      OfferId: null,
      OfferNumber: null,
      Premium: null,
      ErrorMessage: null,
      ExecutionTimeInSec: null,
      DriverAndPassengerInsurancePremium: null,
      RoadAssistancePremium: null,
      TotalPremium: null,
    },
    BTA: {
      IncludeExtendedRoadAssistance: null,
      IncludeDriverAccidentInsurance: null,
      IncludeDriverAndPassingerAccidentInsurance: null,
      OfferId: null,
      OfferNumber: null,
      Premium: null,
      ErrorMessage: null,
      ExecutionTimeInSec: null,
      DriverAndPassengerInsurancePremium: null,
      RoadAssistancePremium: null,
      TotalPremium: null,
    },
    Compensa: {
      IncludeExtendedRoadAssistance: null,
      IncludeDriverAccidentInsurance: null,
      IncludeDriverAndPassingerAccidentInsurance: null,
      OfferId: null,
      OfferNumber: null,
      Premium: null,
      ErrorMessage: null,
      ExecutionTimeInSec: null,
      DriverAndPassengerInsurancePremium: null,
      RoadAssistancePremium: null,
      TotalPremium: null,
    },
    Ergo: {
      IncludeExtendedRoadAssistance: null,
      IncludeDriverAccidentInsurance: null,
      IncludeDriverAndPassingerAccidentInsurance: null,
      OfferId: null,
      OfferNumber: null,
      Premium: null,
      ErrorMessage: null,
      ExecutionTimeInSec: null,
      DriverAndPassengerInsurancePremium: null,
      RoadAssistancePremium: null,
      TotalPremium: null,
    },
    Gjensidige: {
      IncludeExtendedRoadAssistance: null,
      IncludeDriverAccidentInsurance: null,
      IncludeDriverAndPassingerAccidentInsurance: null,
      OfferId: null,
      OfferNumber: null,
      Premium: null,
      ErrorMessage: null,
      ExecutionTimeInSec: null,
      DriverAndPassengerInsurancePremium: null,
      RoadAssistancePremium: null,
      TotalPremium: null,
    },
    IF: {
      IncludeExtendedRoadAssistance: null,
      IncludeDriverAccidentInsurance: null,
      IncludeDriverAndPassingerAccidentInsurance: null,
      OfferId: null,
      OfferNumber: null,
      Premium: null,
      ErrorMessage: null,
      ExecutionTimeInSec: null,
      DriverAndPassengerInsurancePremium: null,
      RoadAssistancePremium: null,
      TotalPremium: null,
    },
    LietuvosDraudimas: {
      IncludeExtendedRoadAssistance: null,
      IncludeDriverAccidentInsurance: null,
      IncludeDriverAndPassingerAccidentInsurance: null,
      OfferId: null,
      OfferNumber: null,
      Premium: null,
      ErrorMessage: null,
      ExecutionTimeInSec: null,
      DriverAndPassengerInsurancePremium: null,
      RoadAssistancePremium: null,
      TotalPremium: null,
    },
  };
  if (insurerSpecificInfos) {
    const insurerNames = Object.keys(insurerSpecificInfos);
    for (let insurerName of insurerNames) {
      if (insurerSpecificInfos[insurerName]) {
        resultinsurerListItems[insurerName].IncludeExtendedRoadAssistance =
          insurerSpecificInfos[insurerName].IncludeExtendedRoadAssistance;
        resultinsurerListItems[insurerName].IncludeDriverAccidentInsurance =
          insurerSpecificInfos[insurerName].IncludeDriverAccidentInsurance;
        resultinsurerListItems[
          insurerName
        ].IncludeDriverAndPassingerAccidentInsurance =
          insurerSpecificInfos[
            insurerName
          ].IncludeDriverAndPassingerAccidentInsurance;
      }
    }
  }
  if (response) {
    const insurerNames = Object.keys(response);
    for (let insurerName of insurerNames) {
      if (response[insurerName]) {
        resultinsurerListItems[insurerName].OfferId =
          response[insurerName].OfferId;
        resultinsurerListItems[insurerName].OfferNumber =
          response[insurerName].OfferNumber;
        resultinsurerListItems[insurerName].Premium =
          response[insurerName].Premium;
        resultinsurerListItems[insurerName].ErrorMessage =
          response[insurerName].ErrorMessage;
        resultinsurerListItems[insurerName].ExecutionTimeInSec =
          response[insurerName].ExecutionTimeInSec;
        resultinsurerListItems[insurerName].DriverAndPassengerInsurancePremium =
          response[insurerName].DriverAndPassengerInsurancePremium;
        resultinsurerListItems[insurerName].RoadAssistancePremium =
          response[insurerName].RoadAssistancePremium;
        resultinsurerListItems[insurerName].TotalPremium =
          response[insurerName].TotalPremium;
      }
    }
  }
  return resultinsurerListItems;
};
